import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import axiosInstance from "../../api/axiosInstance";

const COLORS = {
  gray:   '#5B6B79',
  blue:   '#4680ff',
  green:  '#2CA87F',
  red:    '#9E2B25',
  orange: '#F29E4C',
  purple: '#7C3AED',
}

const thStyle = {
  fontSize: 11, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase',
  letterSpacing: '0.05em', padding: '8px 12px', background: '#f8f9fb',
  borderBottom: '1px solid #e8ecf0', whiteSpace: 'nowrap',
}
const tdStyle = {
  fontSize: 12, color: '#2d3748', padding: '7px 12px',
  verticalAlign: 'middle', borderBottom: '1px solid #f0f2f5',
}

const Badge = ({ label, bg, color }) => (
  <span style={{
    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
    background: bg || '#e8ecf0', color: color || COLORS.gray, whiteSpace: 'nowrap',
  }}>{label}</span>
)

const statusMeta = (status) => {
  if (status === 'accepted') return { bg: '#dcfce7', color: '#166534' }
  if (status === 'rejected') return { bg: '#fee2e2', color: '#991b1b' }
  return { bg: '#fef9c3', color: '#854d0e' } // pending
}

const SummaryCard = ({ icon, label, value, color, sub }) => (
  <div style={{
    background: '#fff', borderRadius: 10, border: `1px solid ${color}33`,
    padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, flex: 1,
  }}>
    <div style={{
      width: 44, height: 44, borderRadius: 10, background: `${color}15`,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <i className={icon} style={{ color, fontSize: 22 }} />
    </div>
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: COLORS.gray, marginTop: 1 }}>{sub}</div>}
    </div>
  </div>
)

export default function CompanyClaims() {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const { state } = useLocation();
  const companyFromState = state?.company;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [claims, setClaims] = useState([]);
  const [search, setSearch] = useState("");
  const [companyName] = useState(companyFromState?.companyName || "");

  const fetchClaimsForCompany = async () => {
    try {
      setLoading(true);
      setError(false);
      const { data } = await axiosInstance.get(`getClaimsByCompanyId/${companyId}`);
      const rows = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setClaims(rows.map(c => ({
        id: c.id,
        claim_id: c.claim_id,
        claim_no: c.claim_number || c.claim_no || c.claimId,
        date: c.created_at || c.date,
        patient_name: c.patient_name || c.patientName,
        policy_number: c.policy_number || c.policyId,
        amount: Number(c.insurance_amount ?? c.amount ?? 0),
        status: (c.status || 'pending').toLowerCase(),
      })));
    } catch (e) {
      console.error('Error loading company claims:', e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClaimsForCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return claims;
    return claims.filter(c =>
      (c.claim_no && String(c.claim_no).toLowerCase().includes(q)) ||
      (c.patient_name && c.patient_name.toLowerCase().includes(q)) ||
      (c.policy_number && String(c.policy_number).toLowerCase().includes(q))
    );
  }, [search, claims]);

  // Summary totals
  const pendingTotal   = claims.filter(c => c.status === 'pending').reduce((s, c) => s + c.amount, 0)
  const acceptedTotal  = claims.filter(c => c.status === 'accepted').reduce((s, c) => s + c.amount, 0)
  const pendingCount   = claims.filter(c => c.status === 'pending').length
  const acceptedCount  = claims.filter(c => c.status === 'accepted').length
  const rejectedCount  = claims.filter(c => c.status === 'rejected').length

  const updateClaimStatus = async (claimId, newStatus) => {
    try {
      const { data } = await axiosInstance.put(`updateInsuranceClaimStatus/${claimId}`, { status: newStatus });
      if (data?.success) {
        setClaims(prev => prev.map(c => c.id === claimId ? { ...c, status: newStatus.toLowerCase() } : c));
        Swal.fire('Success', 'Status updated successfully', 'success');
      } else {
        Swal.fire('Error', data?.message || 'Failed to update status', 'error');
      }
    } catch (e) {
      console.error('Update claim status error:', e);
      Swal.fire('Error', 'Server error while updating status', 'error');
    }
  };

  return (
    <div className="pc-container">
      <div className="pc-content">

        {/* ── Summary Cards ── */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <SummaryCard
            icon="ti ti-clock-pause"
            label="Pending Balance"
            value={`${pendingTotal.toFixed(2)} BHD`}
            color={COLORS.orange}
            sub={`${pendingCount} pending claim${pendingCount !== 1 ? 's' : ''}`}
          />
          <SummaryCard
            icon="ti ti-circle-check"
            label="Accepted Total"
            value={`${acceptedTotal.toFixed(2)} BHD`}
            color={COLORS.green}
            sub={`${acceptedCount} accepted claim${acceptedCount !== 1 ? 's' : ''}`}
          />
          <SummaryCard
            icon="ti ti-files"
            label="Total Claims"
            value={claims.length}
            color={COLORS.blue}
            sub={`${rejectedCount} rejected`}
          />
        </div>

        {/* ── Main Table Card ── */}
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', overflow: 'hidden' }}>

          {/* Header */}
          <div style={{ padding: '12px 20px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => navigate(-1)}
              style={{
                width: 30, height: 30, borderRadius: 8, border: '1px solid #e8ecf0',
                background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = COLORS.blue + '22'; e.currentTarget.style.color = COLORS.blue }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fb'; e.currentTarget.style.color = COLORS.gray }}
            >
              <i className="ti ti-arrow-left" />
            </button>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: `${COLORS.blue}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="ti ti-building-bank" style={{ color: COLORS.blue, fontSize: 17 }} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>
                {companyName || companyFromState?.companyName || `Company #${companyId}`}
              </div>
              <div style={{ fontSize: 11, color: COLORS.gray }}>Insurance claims</div>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <Badge label={`${filtered.length} records`} bg={`${COLORS.blue}15`} color={COLORS.blue} />
            </div>
          </div>

          {/* Filter Bar */}
          <div style={{
            padding: '10px 20px', borderBottom: '1px solid #e8ecf0',
            background: '#fafbfc', display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{ position: 'relative' }}>
              <i className="ti ti-search" style={{
                position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                color: COLORS.gray, fontSize: 13, pointerEvents: 'none',
              }} />
              <input
                type="text"
                placeholder="Search by claim, patient, policy…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{
                  height: 32, borderRadius: 7, border: '1px solid #dde3eb',
                  paddingLeft: 28, paddingRight: 10, fontSize: 12, color: '#2d3748',
                  outline: 'none', background: '#fff', width: 260,
                }}
              />
            </div>
          </div>

          {/* Table */}
          <div style={{ padding: '14px 20px' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: COLORS.gray }}>
                <div className="spinner-border" role="status"
                  style={{ color: COLORS.blue, width: 28, height: 28, borderWidth: 3 }} />
                <div style={{ marginTop: 10, fontSize: 13 }}>Loading claims…</div>
              </div>
            ) : error ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: COLORS.red, fontSize: 13 }}>
                <i className="ti ti-alert-circle" style={{ fontSize: 28, display: 'block', marginBottom: 6 }} />
                Failed to load data
              </div>
            ) : (
              <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e8ecf0' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, borderLeft: `3px solid ${COLORS.blue}` }}>#</th>
                      <th style={thStyle}>Claim ID</th>
                      <th style={thStyle}>Date</th>
                      <th style={thStyle}>Patient</th>
                      <th style={thStyle}>Policy #</th>
                      <th style={thStyle}>Ins. Amount</th>
                      <th style={thStyle}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ ...tdStyle, textAlign: 'center', padding: '36px 0', color: COLORS.gray }}>
                          <i className="ti ti-database-off" style={{ fontSize: 28, display: 'block', marginBottom: 6, opacity: 0.4 }} />
                          No claims found
                        </td>
                      </tr>
                    ) : filtered.map((row, i) => {
                      const sm = statusMeta(row.status)
                      const rowBg = i % 2 === 0 ? '#fff' : '#fafbfc'
                      return (
                        <tr key={row.id}
                          style={{ background: rowBg }}
                          onMouseEnter={e => e.currentTarget.style.background = `${COLORS.blue}08`}
                          onMouseLeave={e => e.currentTarget.style.background = rowBg}
                        >
                          <td style={{ ...tdStyle, color: COLORS.gray, fontSize: 11 }}>{i + 1}</td>
                          <td style={{ ...tdStyle, fontWeight: 600, color: COLORS.blue }}>
                            {row.claim_no || `C${String(row.claim_id || row.id).padStart(5, '0')}`}
                          </td>
                          <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                            {row.date ? new Date(row.date).toLocaleDateString('en-GB') : '—'}
                          </td>
                          <td style={tdStyle}>{row.patient_name || '—'}</td>
                          <td style={tdStyle}>
                            <code style={{ fontSize: 11, color: '#555', background: '#f4f4f8', padding: '1px 5px', borderRadius: 4 }}>
                              {row.policy_number || '—'}
                            </code>
                          </td>
                          <td style={{ ...tdStyle, fontWeight: 600, color: COLORS.green }}>
                            {row.amount != null ? `${row.amount.toFixed(2)} BHD` : '—'}
                          </td>
                          <td style={tdStyle}>
                            {row.status === 'accepted' ? (
                              <Badge label="Accepted" bg={sm.bg} color={sm.color} />
                            ) : (
                              <select
                                value={row.status || 'pending'}
                                disabled={row.status === 'accepted'}
                                onChange={async e => await updateClaimStatus(row.id, e.target.value)}
                                style={{
                                  height: 26, borderRadius: 6, border: `1px solid ${sm.color}55`,
                                  background: sm.bg, color: sm.color, fontSize: 11, fontWeight: 600,
                                  padding: '0 6px', cursor: 'pointer', outline: 'none',
                                }}
                              >
                                <option value="pending">Pending</option>
                                <option value="accepted">Accepted</option>
                                <option value="rejected">Rejected</option>
                              </select>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}


